// The survey data panel: a survey's shots as they were recorded (from, to, length, azimuth, inclination, LRUDs,
// flags, comments), docked under the 3D view.  Its shots are drawn bright on the map with every station named;
// clicking a row shows that shot, clicking a station on the map finds its row (and opens its survey if need be).
import { useState, useEffect, useRef } from 'preact/hooks';
import { html } from './common.js';
import { get } from '../api.js';
import { surveyData, model, viewer, selection, setFilter, feat } from '../store.js';

const FT = 0.3048;
const files = new Map();                       // file id -> data (one fetch per file per visit)
async function fileData(id) {
  if (!files.has(id)) files.set(id, get(`/api/survey-files/${id}/data`).catch((e) => { files.delete(id); throw e; }));
  return files.get(id);
}
export function forgetSurveyData() { files.clear(); }

const FLAG = { L: 'left out of the length', P: 'left out of the plot', X: 'left out entirely', C: 'not adjusted (closure)' };
const n1 = (v) => (v == null ? '' : (+v).toFixed(1));
const ang = (a, b) => [a, b].filter(v => v != null).map(n1).join(' / ');

// the model's surveys from this file, in file order: k-th survey in the file -> model survey index
function modelIndexes(M, fileId) { return M.surveys.map((s, i) => [s, i]).filter(([s]) => s.file_id === fileId).map(([, i]) => i); }

export function SurveyDataPanel() {
  const st = surveyData.value, M = model.value;
  const [data, setData] = useState(null), [err, setErr] = useState(null);
  const [k, setK] = useState(0), [row, setRow] = useState(null), [units, setUnits] = useState(() => { try { return localStorage.getItem('atlas.sd.units') || 'ft'; } catch { return 'ft'; } });
  const [height, setHeight] = useState(() => { try { return +localStorage.getItem('atlas.sd.h') || 300; } catch { return 300; } });
  const body = useRef(), heightRef = useRef(height); heightRef.current = height;

  // open: a file, and which survey in it (from the model's index), and maybe a station to find
  useEffect(() => {
    if (!st?.fileId) return;
    let live = true;
    setErr(null);
    fileData(st.fileId).then((d) => {
      if (!live) return;
      setData(d);
      const idx = M ? modelIndexes(M, st.fileId) : [];
      let kk = st.si != null && idx.includes(st.si) ? idx.indexOf(st.si) : (st.k ?? 0);
      if (st.station) {                                 // the survey in this file that has the station, if not that one
        const has = (s) => s.shots.some(x => x.fr_key === st.station || x.to_key === st.station);
        if (!has(d.surveys[kk] || { shots: [] })) { const j = d.surveys.findIndex(has); if (j >= 0) kk = j; }
      }
      setK(Math.min(kk, d.surveys.length - 1));
      const S = d.surveys[kk];
      const r = st.station && S ? S.shots.findIndex(x => x.fr_key === st.station || x.to_key === st.station) : -1;
      setRow(r >= 0 ? r : null);
    }).catch(e => live && setErr(e.message));
    return () => { live = false; };
  }, [st?.fileId, st?.si, st?.station, st?.t]);

  useEffect(() => {
    document.body.classList.add('has-sdata');
    document.body.style.setProperty('--sdata-h', `${height}px`);
    return () => document.body.classList.remove('has-sdata');
  }, [height]);
  useEffect(() => () => viewer?.setSurveyFocus(null), []);

  const S = data?.surveys?.[k];
  const idx = (name) => (name ? viewer?.nameIdx?.get(name) : undefined);
  // draw it on the map
  useEffect(() => {
    if (!S || !viewer) return;
    const stations = new Set(), shots = [];
    for (const x of S.shots) {
      const a = idx(x.fr_key), b = idx(x.to_key);
      if (a != null) stations.add(a); if (b != null) stations.add(b);
      if (a != null && b != null && a !== b) shots.push([a, b]);
    }
    const r = row != null ? S.shots[row] : null, a = r && idx(r.fr_key), b = r && idx(r.to_key);
    viewer.setSurveyFocus({ stations: [...stations], shots, active: a != null && b != null ? [a, b] : null });
  }, [S, row]);
  // keep the chosen row in view
  useEffect(() => { if (row != null) body.current?.querySelector(`tr[data-r="${row}"]`)?.scrollIntoView({ block: 'nearest' }); }, [row, k, data]);

  // a station clicked on the map: find it here, in another survey of this file, or in its own survey's file
  useEffect(() => {
    const sel = selection.value;
    if (!sel || sel.type !== 'station' || !data || !M) return;
    const name = sel.name, here = (s) => s.shots.findIndex(x => x.fr_key === name || x.to_key === name);
    if (S && here(S) >= 0) {
      const cur = row != null ? S.shots[row] : null;
      if (!(cur && (cur.fr_key === name || cur.to_key === name))) setRow(here(S));
      return;
    }
    const j = data.surveys.findIndex(s => here(s) >= 0);
    if (j >= 0) { setK(j); setRow(here(data.surveys[j])); return; }
    const si = viewer.stSurvey?.[viewer.nameIdx.get(name)], sv = M.surveys[si];
    if (sv?.file_id) surveyData.value = { fileId: sv.file_id, si, station: name, t: Date.now() };
  }, [selection.value]);

  const resize = (e) => {
    e.preventDefault();
    const y0 = e.clientY, h0 = height;
    const mv = (ev) => setHeight(Math.max(160, Math.min(window.innerHeight - 160, h0 - (ev.clientY - y0))));
    const up = () => { window.removeEventListener('pointermove', mv); window.removeEventListener('pointerup', up);
      try { localStorage.setItem('atlas.sd.h', String(Math.round(heightRef.current))); } catch { /* private mode */ } };
    window.addEventListener('pointermove', mv); window.addEventListener('pointerup', up);
  };
  const close = () => { viewer?.setSurveyFocus(null); surveyData.value = null; };
  const choose = (r) => {
    setRow(r);
    const x = S.shots[r], a = idx(x.fr_key), b = idx(x.to_key);
    if (a != null && b != null) viewer.flyToShot(a, b); else if (a != null || b != null) viewer.flyToStation(x.fr_key || x.to_key);
  };
  const station = (name, key) => (key ? html`<a href="#" title="Open this station" onClick=${(e) => { e.preventDefault(); e.stopPropagation();
    const s = viewer.flyToStation(key); if (s) selection.value = s; }}>${name}</a>` : html`<span class="muted" title="Not placed in the 3D model">${name}</span>`);
  const onKey = (e) => {
    if (!S || !['ArrowDown', 'ArrowUp'].includes(e.key)) return;
    e.preventDefault();
    const r = Math.max(0, Math.min(S.shots.length - 1, (row ?? -1) + (e.key === 'ArrowDown' ? 1 : -1))); choose(r);
  };
  const U = units === 'ft' ? 1 : FT, u = units;
  const si = M && st ? modelIndexes(M, st.fileId)[k] : null;
  const lrud = (v) => (v == null ? '' : (v * U).toFixed(1));

  return html`<section class="sd-panel" style=${`height:${height}px`} aria-label="Survey data">
    <div class="profile-grip" onPointerDown=${resize} title="Drag to resize"></div>
    <header>
      <b>Survey data</b>
      ${data && html`<select value=${k} onChange=${(e) => { setK(+e.target.value); setRow(null); }} style="width:auto;max-width:260px" aria-label="Survey">
        ${data.surveys.map((s, i) => html`<option value=${i}>${s.name || `Survey ${i + 1}`}${s.date ? ' · ' + s.date : ''}</option>`)}</select>
        <button class="btn sm" disabled=${k <= 0} onClick=${() => { setK(k - 1); setRow(null); }} aria-label="Previous survey">‹</button>
        <button class="btn sm" disabled=${k >= data.surveys.length - 1} onClick=${() => { setK(k + 1); setRow(null); }} aria-label="Next survey">›</button>
        <span class="tiny muted">${data.file.name}${data.file.cave ? ' · ' + data.file.cave : ''}</span>`}
      <span style="flex:1"></span>
      <span class="seg" style="display:inline-grid;grid-auto-columns:auto">${['ft', 'm'].map(x => html`<button class=${units === x ? 'on' : ''}
        onClick=${() => { setUnits(x); try { localStorage.setItem('atlas.sd.units', x); } catch { /* private */ } }}>${x}</button>`)}</span>
      ${si != null && si >= 0 && html`<button class="btn sm" onClick=${() => { setFilter({ surveys: new Set([si]) }); viewer.flyToSurveys(new Set([si])); }}>Isolate on map</button>`}
      ${data && html`<a class="btn sm ghost" href=${`/api/survey-files/${data.file.id}/download`}>.dat</a>`}
      <button class="btn sm" onClick=${close} aria-label="Close survey data">Close</button>
    </header>
    ${err ? html`<div class="err" style="padding:10px">${err}</div>` : !S ? html`<div class="muted" style="padding:10px">Loading…</div>` : html`
      <div class="sd-meta small">
        ${S.date && html`<span><b>${S.date}</b></span>`}
        ${S.team && html`<span title="Team">${S.team}</span>`}
        <span>${S.shots.length} shots · ${(S.length_ft * (units === 'ft' ? 1 : FT)).toFixed(0)} ${u}</span>
        <span title="Added to the compass readings to give true north">declination ${n1(S.decl)}°</span>
        ${(S.corr.some(c => c) || S.corr2.some(c => c)) && html`<span title="Instrument corrections: compass, clino, tape; back-sight compass, clino">corrections ${S.corr.map(n1).join(', ')}${S.corr2.some(c => c) ? ' · BS ' + S.corr2.map(n1).join(', ') : ''}</span>`}
        ${S.comment && html`<span class="muted">${S.comment}</span>`}
      </div>
      <div class="sd-body" ref=${body} tabindex="0" onKeyDown=${onKey}>
        <table class="t sd-table"><thead><tr>
          <th>From</th><th>To</th><th class="r">Length (${u})</th><th class="r">Azimuth°</th><th class="r">Incl.°</th>
          <th class="r">L</th><th class="r">U</th><th class="r">D</th><th class="r">R</th><th>Flags</th><th>Comment</th></tr></thead>
          <tbody>${S.shots.map((x, r) => html`<tr data-r=${r} class=${(row === r ? 'on ' : '') + (x.flags.includes('X') ? 'muted' : '')} onClick=${() => choose(r)}>
            <td>${station(x.fr, x.fr_key)}</td><td>${station(x.to, x.to_key)}</td>
            <td class="r num">${(x.len * U).toFixed(2)}</td><td class="r num">${ang(x.az, x.az2)}</td><td class="r num">${ang(x.inc, x.inc2)}</td>
            <td class="r num">${lrud(x.L)}</td><td class="r num">${lrud(x.U)}</td><td class="r num">${lrud(x.D)}</td><td class="r num">${lrud(x.R)}</td>
            <td title=${[...x.flags].map(f => FLAG[f]).filter(Boolean).join('; ')}>${x.flags}</td><td class="sd-c">${x.comment}</td></tr>`)}</tbody></table>
        <div class="tiny muted" style="padding:6px 8px">As recorded: azimuths are magnetic (back-sights after the slash), lengths and LRUDs in ${units === 'ft' ? 'feet' : 'metres'}.
          Click a row to see the shot on the map, a station to open it. ↑ ↓ step through the shots.${feat('xsections') ? ' Cross-sections between stations: open a station and choose Cross-section….' : ''}</div>
      </div>`}
  </section>`;
}

